import React from "react";

import classNames from "classnames";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ReactSelect from "react-select";

import ErrorMessage from "./ErrorMessage";
import FieldContainer from "./FieldContainer";
import Label from "./Label";
import { containerClasses, wrapperClasses } from "./styles";

import type { JSX } from "react";
import type { Options, PropsValue, StylesConfig } from "react-select";

interface Option {
  value: string;
  label: string;
}

interface SelectProps<T> {
  name: string;
  label?: string | null;
  icon?: JSX.Element;
  isClearable?: boolean;
  isRequired?: boolean;
  isLoading?: boolean;
  isMulti?: boolean;
  isReadOnly?: boolean;
  options?: T[];
  noOptionsMessage?: string;
  sublabel?: string;
  defaultValue?: PropsValue<T>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  styles?: StylesConfig<any, any>;
  placeholder?: string | null;
  className?: string;
  withoutBorder?: boolean;
  isCompact?: boolean;
  isPortal?: boolean;
  isOptionDisabled?: (option: T, selectValue: Options<T>) => boolean;
  menuIsOpen?: boolean;
  isNoWrap?: boolean;
}

interface SelectStatus {
  isFocused: boolean;
  isSelected: boolean;
  isDisabled: boolean;
}
type SelectClassNames = Record<
  string,
  ({ isFocused, isSelected }: SelectStatus) => string
>;

// ? Source https://www.jussivirtanen.fi/writing/styling-react-select-with-tailwind

export const controlStyles = {
  base: "!min-h-[35px] ring-1 ring-inset ring-input flex border-input py-[0.3125rem] px-[0.46875rem] block w-full rounded-md text-sm text-foreground hover:cursor-pointer",
  focus: "ring-2 ring-primary",
  nonFocus: "ring",
  isReadOnly: "bg-muted cursor-not-allowed",
  withIcon: "pl-[2.1875rem]",
  withoutIcon: "pl-[0.46875rem]",
  withoutBorder: "!ring-0",
  isError: "ring-destructive",
};
export const placeholderStyles =
  "text-muted-foreground py-[0.078125rem] truncate w-full absolute";
export const selectInputStyles = "py-[0.078125rem] !visible truncate";
export const valueContainerStyles = "gap-[0.15625rem]";
export const singleValueStyles = "leading-7 absolute";
export const multiValueStyles = ({
  isReadOnly,
}: {
  isReadOnly?: boolean;
}): string =>
  classNames(
    "flex items-center bg-muted rounded-[0.15625rem] pl-1.25 gap-[0.234375rem]",
    isReadOnly ? "pr-1.25 bg-transparent" : "pr-[0.15625rem]",
  );
export const multiValueLabelStyles =
  "leading-6 overflow-visible pb-[0.078125rem]";
export const multiValueRemoveStyles = ({
  isReadOnly,
}: {
  isReadOnly?: boolean;
}): string =>
  classNames(
    isReadOnly
      ? "hidden"
      : "border border-border bg-card hover:bg-destructive/10 hover:text-destructive text-muted-foreground hover:border-destructive/30 rounded-[0.234375rem]",
  );
export const indicatorsContainerStyles = "gap-[0.15625rem]";
export const clearIndicatorStyles =
  "text-muted-foreground p-[0.15625rem] rounded-md hover:bg-destructive/10 hover:text-destructive";
export const indicatorSeparatorStyles = "bg-border-strong";
export const dropdownIndicatorStyles =
  "p-[0.15625rem] hover:bg-muted text-muted-foreground rounded-md hover:text-foreground";
export const menuStyles =
  "p-[0.15625rem] mt-[0.3125rem] border border-border bg-card rounded-lg";
export const menuPortalStyles = "z-[1600]!";
export const groupHeadingStyles =
  "ml-[0.46875rem] mt-[0.3125rem] mb-[0.15625rem] text-muted-foreground text-sm";
export const optionStyles = {
  selected:
    "text-primary-foreground !bg-primary hover:bg-primary focus:bg-primary ",
  focus: "bg-primary/10",
  base: "hover:cursor-pointer px-[0.46875rem] py-[0.3125rem] rounded break-all",
  disabled: "bg-background !cursor-not-allowed",
};
export const noOptionsMessageStyles =
  "text-muted-foreground p-[0.3125rem] bg-muted border border-dashed border-border rounded-sm";
export const getSelectClassNames = ({
  isInvalid,
  icon,
  isReadOnly,
  withoutBorder,
}:
  | {
      isInvalid?: boolean;
      icon?: JSX.Element;
      isReadOnly?: boolean;
      withoutBorder?: boolean;
    }
  | undefined = {}): SelectClassNames => ({
  container: () => "w-full",
  control: ({ isFocused }: SelectStatus) =>
    classNames(
      isInvalid && controlStyles.isError,
      isFocused ? controlStyles.focus : controlStyles.nonFocus,
      icon ? controlStyles.withIcon : controlStyles.withoutIcon,
      controlStyles.base,
      isReadOnly && controlStyles.isReadOnly,
      withoutBorder && controlStyles.withoutBorder,
    ),
  input: () => selectInputStyles,
  placeholder: () => placeholderStyles,
  valueContainer: () => valueContainerStyles,
  singleValue: () => singleValueStyles,
  multiValue: () => multiValueStyles({ isReadOnly }),
  multiValueLabel: () => multiValueLabelStyles,
  multiValueRemove: () => multiValueRemoveStyles({ isReadOnly }),
  indicatorsContainer: () =>
    isReadOnly ? "!hidden" : indicatorsContainerStyles,
  clearIndicator: () => clearIndicatorStyles,
  indicatorSeparator: () => indicatorSeparatorStyles,
  dropdownIndicator: () => dropdownIndicatorStyles,
  menu: () => menuStyles,
  menuPortal: () => menuPortalStyles,
  groupHeading: () => groupHeadingStyles,
  option: ({ isFocused, isSelected, isDisabled }: SelectStatus) =>
    classNames(
      isFocused && optionStyles.focus,
      isSelected && optionStyles.selected,
      isDisabled && optionStyles.disabled,
      optionStyles.base,
    ),
  noOptionsMessage: () => noOptionsMessageStyles,
});

export default function Select<T extends Option>({
  icon,
  name,
  label,
  sublabel,
  isRequired,
  isClearable,
  options,
  styles,
  isMulti,
  isReadOnly,
  defaultValue,
  noOptionsMessage,
  placeholder,
  isLoading,
  className,
  withoutBorder,
  isCompact,
  isPortal,
  isOptionDisabled,
  isNoWrap,
}: SelectProps<T>): JSX.Element {
  const { getFieldState, control } = useFormContext();
  const { t } = useTranslation();

  const isInvalid = getFieldState(name)?.invalid;

  const selectClassNames = getSelectClassNames({
    isInvalid,
    icon,
    isReadOnly,
    withoutBorder,
  });

  const defaultNoOptionsMessage = t("noOptions", { ns: "forms" });

  const getMultipleValues = (
    selectedValueList: Option[],
  ): Option[] | undefined =>
    selectedValueList?.map(
      (selected) =>
        options?.find((option) => option.value === selected?.value) ?? selected,
    );

  const getSingleValue = (selectedValue: Option): Option | null =>
    options?.find((option) => option.value === selectedValue?.value) ?? null;

  return (
    <FieldContainer className={className} isNoWrap={isNoWrap}>
      {label ? (
        <Label
          isCompact={isCompact}
          isRequired={isRequired}
          label={label}
          name={name}
          sublabel={sublabel}
        />
      ) : null}
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState: { error } }): JSX.Element => (
          <div className={wrapperClasses({ isCompact })}>
            <div className={containerClasses({ isFullWidth: true })}>
              <div className="text-muted-foreground pointer-events-none absolute inset-y-0 left-0 flex h-8.75 items-center pl-[0.46875rem]">
                {icon}
              </div>
              <ReactSelect
                unstyled
                classNames={selectClassNames}
                defaultValue={defaultValue}
                isClearable={isClearable}
                isDisabled={isReadOnly}
                isLoading={isLoading}
                isMulti={isMulti}
                menuPortalTarget={isPortal ? document.body : null}
                menuPosition={isPortal ? "fixed" : "absolute"}
                options={options}
                noOptionsMessage={({ inputValue }): string | false =>
                  !inputValue && (noOptionsMessage ?? defaultNoOptionsMessage)
                }
                placeholder={
                  placeholder ??
                  t("select", {
                    ns: "forms",
                  })
                }
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...field}
                isOptionDisabled={
                  isOptionDisabled as (
                    option: Option,
                    selectValue: Options<Option>,
                  ) => boolean
                }
                styles={{
                  control: () => ({}), // Only way to remove the default styles
                  ...styles,
                }}
                value={
                  isMulti
                    ? getMultipleValues(field.value)
                    : getSingleValue(field.value)
                }
              />
            </div>
            <ErrorMessage error={error} />
          </div>
        )}
        rules={{
          required:
            isRequired &&
            t("requiredField", {
              ns: "editor-errors",
            }),
        }}
      />
    </FieldContainer>
  );
}
